import {
  Button,
  createStyles,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React, {useState} from 'react';

import {MAX_AUTOMATION_TEMPLATE_DESCRIPTION_LENGTH} from 'analysis-tool/AnalysisToolConstants';
import {
  updateAnalysisTemplateMetadata,
  UpdateAnalysisTemplateMetadataParams,
} from 'analysis-tool/templates/AnalysisTemplateActions';
import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import BenchlingConfig from 'BenchlingConfig';
import {getDisabledTooltipButtonProps} from 'lib/a11y/ButtonA11y';
import TemplateCollectionSelector from 'notebook/entry-templates/collections/TemplateCollectionSelector';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import ModalFooter from 'ui-library/modals/ModalFooter';
import type {RunModalProps} from 'ui-library/runModal';
import {readableTime} from 'util/DateUtil';
import fuzzydate from 'util/fuzzydate';
import {capitalize} from 'util/NameUtil';

export type AnalysisTemplateMetadataModalProps = RunModalProps<void> & {
  analysisTemplate: AnalysisTemplateGraphQL;
};

const useStyles = createStyles(() => ({
  input: {
    resize: 'vertical',
  },
  descriptionText: {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1,
  },
}));

function MetadataField({label, children}: {children: React.ReactNode; label: string}): JSX.Element {
  return (
    <Stack spacing="xs">
      <Text size="sm" weight={600}>
        {label}
      </Text>
      {children}
    </Stack>
  );
}

export default function AnalysisTemplateMetadataModal({
  analysisTemplate,
  close,
}: AnalysisTemplateMetadataModalProps): JSX.Element {
  const [name, setName] = useState(analysisTemplate.name);
  const [templateCollectionId, setTemplateCollectionId] = useState(analysisTemplate.templateCollection.id);
  const [description, setDescription] = useState(analysisTemplate.description);
  const [isSaving, setIsSaving] = useState(false);

  const {classes} = useStyles();

  const writable = analysisTemplate.permissions.writable;
  const movable = analysisTemplate.permissions.movable ?? false;
  const canSave = writable || movable;

  const trimmedName = name.trim();
  const isNameBlank = trimmedName === '';

  const normalizedDescription = description?.trim() || null;
  const descriptionLength = normalizedDescription?.length ?? 0;
  const isDescriptionTooLong = descriptionLength > MAX_AUTOMATION_TEMPLATE_DESCRIPTION_LENGTH;

  const canSubmit = !writable || (!isNameBlank && !isDescriptionTooLong);

  const disabledReasonId = useId();
  const disabledTooltip = !writable
    ? null
    : isNameBlank
      ? 'Template name must be non-empty.'
      : isDescriptionTooLong
        ? `Description must be at most ${MAX_AUTOMATION_TEMPLATE_DESCRIPTION_LENGTH} characters.`
        : null;

  async function handleSave(): Promise<void> {
    if (!canSave) {
      close();
      return;
    }

    const params: UpdateAnalysisTemplateMetadataParams = {};
    if (writable && trimmedName !== analysisTemplate.name) {
      params.name = trimmedName;
    }
    if (movable && templateCollectionId !== analysisTemplate.templateCollection.id) {
      params.templateCollectionId = templateCollectionId;
    }

    if (writable && normalizedDescription !== analysisTemplate.description) {
      params.description = normalizedDescription;
    }

    if (Object.keys(params).length === 0) {
      close();
      return;
    }

    setIsSaving(true);
    try {
      await updateAnalysisTemplateMetadata(analysisTemplate, params);
    } finally {
      setIsSaving(false);
    }
    close();
  }

  return (
    <Stack spacing="md" {...getTestProps({component: 'AnalysisTemplateMetadataModal', element: 'root'})}>
      <SimpleGrid cols={2} spacing="md">
        {writable ? (
          <TextInput
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.currentTarget.value);
            }}
            error={isNameBlank}
            data-autofocus={true}
          />
        ) : (
          <MetadataField label="Name">
            <Text>{analysisTemplate.name}</Text>
          </MetadataField>
        )}
        {movable ? (
          <Stack spacing="xs">
            <Text size="sm" weight={600}>
              Template collection
            </Text>
            <TemplateCollectionSelector
              searchable={true}
              value={templateCollectionId}
              onChange={(templateCollection) => {
                setTemplateCollectionId(templateCollection.id);
              }}
            />
          </Stack>
        ) : (
          <MetadataField label="Template collection">
            <Text>{analysisTemplate.templateCollection.name}</Text>
          </MetadataField>
        )}
      </SimpleGrid>
      {writable ? (
        <Textarea
          classNames={{input: classes.input}}
          inputWrapperOrder={['label', 'input', 'description']}
          label="Description"
          description={`${descriptionLength} of ${MAX_AUTOMATION_TEMPLATE_DESCRIPTION_LENGTH} allowed characters.`}
          error={isDescriptionTooLong}
          value={description ?? ''}
          onChange={(e) => {
            setDescription(e.currentTarget.value);
          }}
        />
      ) : (
        <MetadataField label="Description">
          <Text
            className={classes.descriptionText}
            {...getTestProps({component: 'AnalysisTemplateMetadataModal', element: 'descriptionText'})}
          >
            {analysisTemplate.description}
          </Text>
        </MetadataField>
      )}
      <SimpleGrid cols={2} spacing="md">
        <MetadataField label="Creator">
          <Text>{analysisTemplate.creator.name}</Text>
        </MetadataField>
        <MetadataField label="Created at">
          <Text>{readableTime(analysisTemplate.createdAt)}</Text>
        </MetadataField>
      </SimpleGrid>
      <SimpleGrid cols={2} spacing="md">
        <MetadataField label="Last modified">
          <Text>{capitalize(fuzzydate(analysisTemplate.modifiedAt))}</Text>
        </MetadataField>
        <MetadataField label="Published at">
          <Text>
            {analysisTemplate.publishedAt != null
              ? readableTime(analysisTemplate.publishedAt)
              : 'Not published'}
          </Text>
        </MetadataField>
      </SimpleGrid>
      <MetadataField label="Step count">
        <Text>
          {analysisTemplate.steps.length} / {BenchlingConfig.PIPELINES_MAX_STEP_COUNT}
        </Text>
      </MetadataField>
      <ModalFooter>
        <Group position="right">
          <Tooltip
            // ButtonWithDisabledReason's tooltip cannot adjust its z-index, so it would render behind the modal.
            events={{focus: true, hover: true, touch: false}}
            label={disabledTooltip ?? ''}
            disabled={canSubmit}
            withinPortal={true}
            zIndex={ZINDEX_FLOATING_UI}
          >
            <Button
              loading={isSaving}
              aria-describedby={canSubmit ? undefined : disabledReasonId}
              {...getDisabledTooltipButtonProps(!canSubmit, handleSave, true)}
            >
              {canSave ? 'Save' : 'Done'}
            </Button>
          </Tooltip>
          {!canSubmit && (
            <span className={classes.visuallyHidden} id={disabledReasonId}>
              {disabledTooltip}
            </span>
          )}
        </Group>
      </ModalFooter>
    </Stack>
  );
}
